import { useMemo } from "react"
import type { Accept } from "react-dropzone"

import { Logger } from "shared/lib/Logger"

import { useAttachmentAcceptedFileTypesQuery } from "entities/attachments/graphql/project-attachments.queries.generated"
import { z } from "zod"

const AcceptedFileTypeSchema = z.record(
  z.string(),
  z.record(z.string(), z.array(z.string()))
)

const defaultAccept = {
  "application/pdf": [".pdf"]
}

const defaultAccepts: Record<string, Accept> = {
  Drawings: defaultAccept,
  Specifications: defaultAccept,
  RoomBook: defaultAccept,
  InstallationSituation: defaultAccept,
  Miscellaneous: defaultAccept
}

export function useProjectAttachmentAcceptedFileTypes() {
  const {
    data,
    loading: isLoading,
    error: acceptedFileTypesError,
    ...rest
  } = useAttachmentAcceptedFileTypesQuery()

  const transformedAcceptedFileTypes = useMemo(() => {
    const fileTypes = data?.attachmentAcceptedFileTypes

    if (!Array.isArray(fileTypes)) {
      return defaultAccepts
    }

    const acceptsByCategory: Record<string, Accept> = {}

    fileTypes.forEach(fileType => {
      fileType.categories?.forEach(category => {
        if (!acceptsByCategory[category]) {
          acceptsByCategory[category] = {}
        }
        const accept = acceptsByCategory[category]
        const extension = `.${fileType.extension}`
        if (!accept[fileType.mimeType]) {
          accept[fileType.mimeType] = [extension]
        } else {
          const existingExtensions = accept[fileType.mimeType]
          if (!existingExtensions.includes(extension)) {
            accept[fileType.mimeType] = [...existingExtensions, extension]
          }
        }
      })
    })

    return acceptsByCategory
  }, [data?.attachmentAcceptedFileTypes])

  const acceptsByCategory: Record<string, Accept> = useMemo(() => {
    const { data: validatedAcceptedFileTypes, error } =
      AcceptedFileTypeSchema.safeParse(transformedAcceptedFileTypes)

    if (
      !transformedAcceptedFileTypes ||
      Object.keys(transformedAcceptedFileTypes).length === 0
    )
      return defaultAccepts

    if (error?.issues) {
      Logger.error(error, "AttachmentAcceptedFileTypesError", {
        transformedAcceptedFileTypes,
        error: error.issues
      })
      return defaultAccepts
    }

    return validatedAcceptedFileTypes || defaultAccepts
  }, [transformedAcceptedFileTypes])

  return {
    attachmentAcceptedFileTypes: transformedAcceptedFileTypes,
    acceptsByCategory,
    isLoading,
    error: !!acceptedFileTypesError,
    ...rest
  }
}
